/**
 * Requirements Generation Dialog Component
 *
 * Shows real-time progress of requirements generation via WebSocket.
 * Auto-closes on success, stays open on error.
 */

import React, {useEffect, useState, useCallback, useRef} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Alert,
  Chip,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
} from '@mui/icons-material';
import {
  createWebSocket,
  getJobStatus,
  ProgressEvent,
} from '../services/agent';

interface RequirementsGenerationDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void; // Callback when requirements generated successfully
  jobId: string | null;
  trialId: string;
  version1: string;
  version2: string;
  /** Dispatcher-mode: external progress messages from useDispatcherJob */
  dispatcherMessages?: ProgressEvent[];
  /** Dispatcher-mode: external status from useDispatcherJob */
  dispatcherStatus?: string;
  /** Dispatcher-mode: external error from useDispatcherJob */
  dispatcherError?: string | null;
}

export const RequirementsGenerationDialog: React.FC<RequirementsGenerationDialogProps> = ({
  open,
  onClose,
  onSuccess,
  jobId,
  trialId,
  version1,
  version2,
  dispatcherMessages,
  dispatcherStatus,
  dispatcherError,
}) => {
  const isDispatcherMode = !!dispatcherMessages;

  const [status, setStatus] = useState<string>('pending');
  const [progressMessages, setProgressMessages] = useState<ProgressEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [ws, setWs] = useState<WebSocket | null>(null);
  const [latestClipboardMessage, setLatestClipboardMessage] = useState<string>('');
  const progressEndRef = useRef<HTMLDivElement>(null);

  const isBusy     = status === 'running' || status === 'connecting';
  const isFailed   = status === 'failed';
  const isTerminal = status === 'failed' || status === 'completed';

  // In dispatcher mode, sync from external state
  useEffect(() => {
    if (isDispatcherMode) {
      if (dispatcherMessages) {
        setProgressMessages(dispatcherMessages);
        // Filter for latest clipboard message
        const clipboardMessages = dispatcherMessages.filter(msg => msg.message.startsWith('📋'));
        if (clipboardMessages.length > 0) {
          setLatestClipboardMessage(clipboardMessages[clipboardMessages.length - 1].message);
        }
      }
      if (dispatcherStatus) setStatus(dispatcherStatus);
      if (dispatcherError !== undefined) setError(dispatcherError ?? null);

      // Auto-close on success in dispatcher mode
      if (dispatcherStatus === 'completed') {
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 1000);
      }
    }
  }, [isDispatcherMode, dispatcherMessages, dispatcherStatus, dispatcherError, onSuccess, onClose]);

  // Handle WebSocket message (legacy job-ID mode only)
  const handleMessage = useCallback((event: ProgressEvent) => {
    if (isDispatcherMode) return; // Skip in dispatcher mode
    setProgressMessages(prev => [...prev, event]);
    setStatus(event.status);

    // Update clipboard message if this is a clipboard event
    if (event.message.startsWith('📋')) {
      setLatestClipboardMessage(event.message);
    }

    // If completed successfully with usable result, auto-close and notify parent
    if (event.status === 'completed' && !event.data?.error) {
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1000);
    }

    // If failed, keep dialog open
    if (event.status === 'failed') {
      setError(event.data?.error || event.message || 'Requirements generation failed');
    }
  }, [isDispatcherMode, onSuccess, onClose]);

  // Handle WebSocket error (legacy mode only)
  const handleError = useCallback(() => {
    if (isDispatcherMode) return;
    setError('Connection lost. Fetching latest status...');

    if (jobId) {
      getJobStatus(jobId).then(job => {
        setStatus(job.status);
        if (job.error) {
          setError(job.error);
        }
        if (job.status === 'completed' && !job.error) {
          setTimeout(() => {
            onSuccess();
            onClose();
          }, 1000);
        }
      });
    }
  }, [isDispatcherMode, jobId, onSuccess, onClose]);

  // Auto-scroll to latest message
  useEffect(() => {
    if (progressEndRef.current) {
      progressEndRef.current.scrollIntoView({behavior: 'smooth'});
    }
  }, [progressMessages]);

  // Connect WebSocket when dialog opens with a jobId (legacy mode only)
  useEffect(() => {
    if (isDispatcherMode || !open || !jobId) return;

    setStatus('pending');
    setProgressMessages([]);
    setError(null);
    setLatestClipboardMessage('');

    const websocket = createWebSocket(jobId, handleMessage, handleError);
    setWs(websocket);

    return () => {
      if (websocket && websocket.readyState === WebSocket.OPEN) {
        websocket.close();
      }
    };
  }, [isDispatcherMode, open, jobId, handleMessage, handleError]);

  // Close WebSocket automatically when status transitions to 'failed' (legacy mode only)
  useEffect(() => {
    if (isDispatcherMode) return;
    if (status === 'failed' && ws && ws.readyState === WebSocket.OPEN) {
      ws.close();
    }
  }, [isDispatcherMode, status, ws]);

  const handleClose = () => {
    // Close WebSocket if still open
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.close();
    }
    onClose();
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'completed':
        return <CheckIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'running':
        return <PendingIcon color="primary" />;
      default:
        return <PendingIcon color="disabled" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'running':
        return 'primary';
      default:
        return 'default';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={isTerminal ? handleClose : undefined}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={isBusy}
    >
      <DialogTitle>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          {getStatusIcon()}
          <Typography variant="h6">
            Generating Requirements
          </Typography>
          <Chip
            label={status}
            size="small"
            color={getStatusColor() as any}
            sx={{ml: 'auto', textTransform: 'capitalize'}}
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
          {trialId} • {version1} → {version2}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {/* Progress indicator */}
        {isBusy && (
          <Box sx={{mb: 2}}>
            <LinearProgress />
          </Box>
        )}

        {/* Error alert */}
        {error && (
          <Alert severity="error" sx={{mb: 2}}>
            {error}
          </Alert>
        )}

        {/* Permanent clipboard status display */}
        {latestClipboardMessage && (
          <Box
            sx={{
              mb: 2,
              p: 2,
              backgroundColor: 'action.hover',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1
            }}
          >
            <Typography variant="body2" color="text.primary">
              {latestClipboardMessage}
            </Typography>
          </Box>
        )}

        {/* Progress messages */}
        <List sx={{maxHeight: 400, overflow: 'auto'}}>
          {progressMessages.map((msg, index) => (
            <ListItem key={index} sx={{py: 0.5}}>
              <ListItemText
                primary={msg.message}
                secondary={new Date(msg.timestamp).toLocaleTimeString()}
                primaryTypographyProps={{variant: 'body2'}}
                secondaryTypographyProps={{variant: 'caption'}}
              />
            </ListItem>
          ))}
          <div ref={progressEndRef} />
        </List>

        {progressMessages.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{textAlign: 'center', py: 2}}>
            Waiting for job to start...
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          variant={isFailed ? 'contained' : 'outlined'}
        >
          {isBusy ? 'Cancel' : 'Close'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

